import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { CircleX } from 'lucide-react'
import {
  MfaApiError,
  otpSendWait,
  requestStepUp,
  resendEmailCode,
  verifyStepUp,
  type StepUpChallenge,
  type StepUpMethod,
} from '../../api/mfa'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { Icon } from '../../platform/icons/Icon'
import { Overlay } from '../../platform/overlay/Overlay'
import { useToast } from '../../platform/feedback/ToastProvider'
import { mfaMessage } from './mfaCopy'
import { resendLabel, useResendCooldown } from './useResendCooldown'
import './mfaSheet.css'

type Props = {
  open: boolean
  onCancel: () => void
  /**
   * The step-up is complete. `token` is good for ONE retry of the gated call
   * (single use, five minutes); the caller must not keep it.
   */
  onVerified: (token: string) => void
}

/** A challenge allows this many wrong answers (backend `MAX_ATTEMPTS_PER_CHALLENGE`); then it is dead. */
const MAX_WRONG_ANSWERS = 3

const PROMPT: Record<StepUpMethod, string> = {
  email: 'For your security, enter the 6-digit code we just emailed you.',
  totp: 'For your security, enter the 6-digit code from the authenticator app you set up in the TrackE5 iOS app.',
  'backup-code': 'Enter one of your recovery codes. Each works once.',
}

const LABEL: Record<StepUpMethod, string> = {
  email: 'Code from your email',
  totp: 'Authenticator code',
  'backup-code': 'Recovery code',
}

type Phase =
  | { kind: 'requesting' }
  /**
   * `codeSent`: an email code actually exists for THIS challenge. Codes are bound to the
   * challenge, so after a send limit on the first send none does (an email from an earlier
   * challenge cannot verify here) and the sheet offers no email field until a send succeeds.
   */
  | { kind: 'entering'; challenge: StepUpChallenge; method: StepUpMethod; codeSent: boolean }
  | { kind: 'unsupported'; challenge: StepUpChallenge }
  | { kind: 'expired' }
  | { kind: 'failed'; message: string }
  /** Refused for 15 minutes: asking again cannot work, so the sheet offers Close only. */
  | { kind: 'locked'; message: string }

/**
 * Confirm it's you, before a factor change — the backend's step-up
 * (`POST /mfa/step-up/request` then `/verify`), as a full-screen sheet (R28).
 *
 * The backend names ONE required factor. **Email** is sent here and entered;
 * **authenticator app** is entered from the backend's own TOTP. Biometric and
 * push cannot be answered in a browser (push is not proof either), so those
 * accounts are told to use the iOS app. A wrong code, a lockout and an expired
 * challenge each have their own message.
 *
 * Nothing typed or received here is logged, cached or sent to analytics (rule 7);
 * the token goes to `onVerified` once and this component forgets it on close.
 */
export function StepUpSheet({ open, onCancel, onVerified }: Props) {
  return (
    <Overlay open={open} onClose={onCancel} label="Confirm it’s you" placement="sheet">
      {open ? <Flow onCancel={onCancel} onVerified={onVerified} /> : null}
    </Overlay>
  )
}

function Flow({ onCancel, onVerified }: Omit<Props, 'open'>) {
  const [phase, setPhase] = useState<Phase>({ kind: 'requesting' })
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const started = useRef(false)
  const wrong = useRef(0)
  const cooldown = useResendCooldown()
  const { start: startCooldown } = cooldown
  const { show } = useToast()

  const begin = useCallback(async () => {
    setPhase({ kind: 'requesting' })
    setError(null)
    setCode('')
    wrong.current = 0
    let challenge: StepUpChallenge | null = null
    try {
      challenge = await requestStepUp()
      if (challenge.required !== 'email' && challenge.required !== 'totp') {
        setPhase({ kind: 'unsupported', challenge })
        return
      }
      if (challenge.required === 'email') {
        await resendEmailCode(challenge.challengeToken)
        startCooldown()
      }
      setPhase({ kind: 'entering', challenge, method: challenge.required, codeSent: challenge.required === 'email' })
    } catch (thrown) {
      const wait = otpSendWait(thrown)
      if (wait !== null) startCooldown(wait)
      if (wait !== null && challenge !== null) {
        // The challenge is live but no email went out for it, and a code is bound to its
        // challenge, so none exists to enter. A recovery code never needs a send; Resend
        // counts down the server's wait.
        setPhase({ kind: 'entering', challenge, method: 'email', codeSent: false })
        setError(mfaMessage(thrown))
        return
      }
      setPhase(
        thrown instanceof MfaApiError && thrown.kind === 'step_up_locked'
          ? { kind: 'locked', message: mfaMessage(thrown) }
          : { kind: 'failed', message: mfaMessage(thrown) },
      )
    }
  }, [startCooldown])

  // StrictMode runs effects twice in dev; the ref keeps that to one challenge (and one email).
  useEffect(() => {
    if (started.current) return
    started.current = true
    void begin()
  }, [begin])

  const entering = phase.kind === 'entering'
  useEffect(() => {
    if (entering) inputRef.current?.focus()
  }, [entering])

  async function verify(event: FormEvent) {
    event.preventDefault()
    if (phase.kind !== 'entering' || busy) return
    setBusy(true)
    setError(null)
    try {
      const done = await verifyStepUp(phase.challenge.challengeToken, phase.method, code.trim())
      if (done) {
        onVerified(phase.challenge.challengeToken)
        return
      }
      setError('That wasn’t enough to confirm it’s you. Try again.')
    } catch (thrown) {
      if (thrown instanceof MfaApiError && thrown.kind === 'expired') {
        setPhase({ kind: 'expired' })
      } else if (thrown instanceof MfaApiError && thrown.kind === 'invalid_code' && ++wrong.current >= MAX_WRONG_ANSWERS) {
        // The backend kills a challenge after three wrong answers but replies as
        // for any wrong code, so the client counts: a fourth try could never work.
        setPhase({ kind: 'expired' })
      } else if (thrown instanceof MfaApiError && thrown.kind === 'step_up_locked') {
        setPhase({ kind: 'locked', message: mfaMessage(thrown) })
      } else setError(mfaMessage(thrown))
    }
    setBusy(false)
    inputRef.current?.focus()
  }

  async function resend() {
    if (phase.kind !== 'entering') return
    // A long send-limit wait can outlive the five-minute challenge; the backend would
    // answer "wrong code", so route to the expired phase, whose Start again makes a fresh one.
    if (phase.challenge.expiresAt.getTime() <= Date.now()) {
      setPhase({ kind: 'expired' })
      return
    }
    setBusy(true)
    setError(null)
    try {
      await resendEmailCode(phase.challenge.challengeToken)
      setPhase({ ...phase, method: 'email', codeSent: true })
      setCode('')
      startCooldown()
      show({ kind: 'success', message: 'We sent a new code.' })
    } catch (thrown) {
      const wait = otpSendWait(thrown)
      if (wait !== null) startCooldown(wait)
      setError(mfaMessage(thrown))
    } finally {
      setBusy(false)
    }
  }

  const problem =
    phase.kind === 'failed' || phase.kind === 'locked'
      ? phase.message
      : phase.kind === 'expired'
        ? 'This confirmation timed out or was used up. Start again.'
        : phase.kind === 'unsupported'
          ? 'Your account confirms changes with a method that only works in the TrackE5 iOS app. Use the app, or use one of your recovery codes here.'
          : null

  /** Switch the open challenge to (or from) answering with a recovery code. */
  function answerWith(method: StepUpMethod) {
    if (phase.kind !== 'entering' && phase.kind !== 'unsupported') return
    setCode('')
    setError(null)
    setPhase({ kind: 'entering', challenge: phase.challenge, method, codeSent: phase.kind === 'entering' && phase.codeSent })
  }

  return (
    <div className="mfa-sheet">
      <h2 className="dialog__title">Confirm it’s you</h2>
      {phase.kind === 'requesting' && <p className="dialog__body">Getting things ready…</p>}
      {problem !== null && (
        <>
          <p className="dialog__error" role="alert">
            <span className="dialog__error-mark">
              <Icon glyph={CircleX} />
            </span>
            {problem}
          </p>
          <div className="dialog__actions">
            {phase.kind === 'unsupported' ? (
              <Button variant="primary" size="page" onClick={() => answerWith('backup-code')}>
                Use a recovery code
              </Button>
            ) : phase.kind === 'locked' ? null : (
              <Button variant="primary" size="page" onClick={() => void begin()}>
                {phase.kind === 'expired' ? 'Start again' : 'Try again'}
              </Button>
            )}
            <Button variant="outline" size="page" onClick={onCancel}>
              {phase.kind === 'locked' ? 'Close' : 'Cancel'}
            </Button>
          </div>
        </>
      )}
      {phase.kind === 'entering' && phase.method === 'email' && !phase.codeSent && (
        <div className="mfa-sheet">
          <p className="dialog__body">
            No code has been sent for this attempt yet. Send a new code when the wait is over, or use a recovery code.
          </p>
          {error !== null && (
            <p className="dialog__error" role="alert">
              <span className="dialog__error-mark">
                <Icon glyph={CircleX} />
              </span>
              {error}
            </p>
          )}
          <div className="dialog__actions">
            <Button
              type="button"
              variant="primary"
              size="page"
              disabled={busy || cooldown.secondsLeft > 0}
              onClick={() => void resend()}
            >
              {resendLabel(cooldown.secondsLeft)}
            </Button>
            <Button type="button" variant="outline" size="page" disabled={busy} onClick={() => answerWith('backup-code')}>
              Use a recovery code
            </Button>
            <Button type="button" variant="outline" size="page" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {phase.kind === 'entering' && !(phase.method === 'email' && !phase.codeSent) && (
        <form className="mfa-sheet" onSubmit={(e) => void verify(e)}>
          <p className="dialog__body">{PROMPT[phase.method]}</p>
          <Field
            label={LABEL[phase.method]}
            ref={inputRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode={phase.method === 'backup-code' ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            maxLength={phase.method === 'backup-code' ? 16 : 6}
            {...(error !== null ? { error } : {})}
          />
          <div className="dialog__actions">
            <Button
              type="submit"
              variant="primary"
              size="page"
              disabled={busy || code.trim().length < (phase.method === 'backup-code' ? 8 : 6)}
            >
              {busy ? 'Checking…' : 'Confirm'}
            </Button>
            {phase.method === 'email' && (
              <Button
                type="button"
                variant="outline"
                size="page"
                disabled={busy || cooldown.secondsLeft > 0}
                onClick={() => void resend()}
              >
                {resendLabel(cooldown.secondsLeft)}
              </Button>
            )}
            {phase.method === 'backup-code' ? (
              phase.challenge.required === 'email' || phase.challenge.required === 'totp' ? (
                <Button
                  type="button"
                  variant="outline"
                  size="page"
                  disabled={busy}
                  onClick={() => answerWith(phase.challenge.required as 'email' | 'totp')}
                >
                  {phase.challenge.required === 'email'
                    ? phase.codeSent
                      ? 'Use the emailed code'
                      : 'Back'
                    : 'Use the authenticator code'}
                </Button>
              ) : null
            ) : (
              <Button type="button" variant="outline" size="page" disabled={busy} onClick={() => answerWith('backup-code')}>
                Use a recovery code
              </Button>
            )}
            <Button type="button" variant="outline" size="page" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}
